/**
 * TeamBuilder.jsx - Fantasy roster builder page.
 *
 * Lets users construct a standard fantasy lineup (QB, RB×2, WR×2, TE, FLEX,
 * K, DST + 6 bench spots) using the current week's ML projections. Rosters
 * can be saved to the backend, loaded back later, and exported to CSV.
 *
 * Interaction model: click a roster slot to activate it, then click a player
 * in the pool on the right to fill it. Clicking a filled slot removes the
 * player and re-activates that slot.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { api } from '../utils/api';
import { Search, X, Download, Save, FolderOpen, Trash2, RefreshCw, Check } from 'lucide-react';

const POSITIONS = ['All', 'QB', 'RB', 'WR', 'TE', 'K', 'DST'];

const POSITION_COLORS = {
  QB:  { bg: 'bg-red-500/20',    text: 'text-red-400',    border: 'border-red-500/30'   },
  RB:  { bg: 'bg-green-500/20',  text: 'text-green-400',  border: 'border-green-500/30' },
  WR:  { bg: 'bg-blue-500/20',   text: 'text-blue-400',   border: 'border-blue-500/30'  },
  TE:  { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/30'},
  K:   { bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/30'},
  DST: { bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500/30'},
};
const posStyle = (pos) => POSITION_COLORS[pos] || { bg: 'bg-white/10', text: 'text-white/60', border: 'border-white/10' };

// Standard fantasy lineup definition — id matches the roster state keys
const SLOTS = [
  { id: 'qb',   label: 'QB',   eligible: ['QB'],                          bench: false },
  { id: 'rb1',  label: 'RB',   eligible: ['RB'],                          bench: false },
  { id: 'rb2',  label: 'RB',   eligible: ['RB'],                          bench: false },
  { id: 'wr1',  label: 'WR',   eligible: ['WR'],                          bench: false },
  { id: 'wr2',  label: 'WR',   eligible: ['WR'],                          bench: false },
  { id: 'te',   label: 'TE',   eligible: ['TE'],                          bench: false },
  { id: 'flex', label: 'FLEX', eligible: ['RB', 'WR', 'TE'],              bench: false },
  { id: 'k',    label: 'K',    eligible: ['K'],                           bench: false },
  { id: 'dst',  label: 'DST',  eligible: ['DST'],                         bench: false },
  { id: 'bn1',  label: 'BN',   eligible: ['QB','RB','WR','TE','K','DST'], bench: true  },
  { id: 'bn2',  label: 'BN',   eligible: ['QB','RB','WR','TE','K','DST'], bench: true  },
  { id: 'bn3',  label: 'BN',   eligible: ['QB','RB','WR','TE','K','DST'], bench: true  },
  { id: 'bn4',  label: 'BN',   eligible: ['QB','RB','WR','TE','K','DST'], bench: true  },
  { id: 'bn5',  label: 'BN',   eligible: ['QB','RB','WR','TE','K','DST'], bench: true  },
  { id: 'bn6',  label: 'BN',   eligible: ['QB','RB','WR','TE','K','DST'], bench: true  },
];

const STARTER_SLOTS = SLOTS.filter(s => !s.bench);
const BENCH_SLOTS   = SLOTS.filter(s =>  s.bench);

const getRisk = (ciLow, ciHigh, proj) => {
  if (ciLow == null || ciHigh == null || !proj) return { label: '—', color: 'text-white/30' };
  const spread = ciHigh - ciLow;
  const pct    = (spread / proj) * 100;
  if (spread > 18 || pct > 150) return { label: 'High', color: 'text-red-400'    };
  if (spread > 10 || pct > 100) return { label: 'Med',  color: 'text-yellow-400' };
  return { label: 'Low', color: 'text-green-400' };
};

// Small circular avatar showing player headshot or initials
const PlayerAvatar = ({ player, size = 8 }) => {
  const s       = posStyle(player.pos);
  const initials = player.name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '?';
  const dstLogo  = player.pos === 'DST' && player.team
    ? `https://a.espncdn.com/i/teamlogos/nfl/500/${player.team.toLowerCase()}.png` : null;
  const imgSrc   = player.imageUrl || dstLogo;
  return (
    <div className={`w-${size} h-${size} rounded-full ${s.bg} border ${s.border} overflow-hidden flex-shrink-0 flex items-center justify-center text-[10px] font-bold ${s.text}`}>
      {imgSrc
        ? <img src={imgSrc} alt={player.name} loading="lazy" className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }}/>
        : initials}
    </div>
  );
};

// Thin horizontal bar showing floor–ceiling range with a projection marker
const ProjBar = ({ player }) => {
  if (!player.ciLow || !player.ciHigh) return null;
  const max      = player.ciHigh * 1.1;
  const floorPct = (player.ciLow  / max) * 100;
  const projPct  = (player.proj   / max) * 100;
  const ceilPct  = (player.ciHigh / max) * 100;
  return (
    <div className="relative h-1.5 w-full rounded-full bg-white/[0.06] mt-1.5 overflow-hidden">
      <div className="absolute h-full rounded-full bg-[#4d7cff]/20"
        style={{ left: `${floorPct}%`, width: `${ceilPct - floorPct}%` }}/>
      <div className="absolute top-0 w-1 h-full bg-[#4d7cff] rounded-full"
        style={{ left: `${projPct - 0.5}%` }}/>
    </div>
  );
};

/**
 * Modal listing all saved teams with load and delete actions.
 */
const MyTeamsModal = ({ teams, onLoad, onDelete, onClose, currentTeamId }) => {
  const [deleting, setDeleting] = useState(null);

  const handleDelete = async (id) => {
    setDeleting(id);
    try { await onDelete(id); }
    finally { setDeleting(null); }
  };

  return (
    <>
      <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm" onClick={onClose}/>
      <div className="fixed inset-0 z-[101] flex items-center justify-center p-4 pointer-events-none">
        <div className="bg-[#0b1120] border border-white/[0.10] rounded-2xl shadow-[0_24px_80px_rgba(0,0,0,0.9)] w-full max-w-lg pointer-events-auto">
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.07]">
            <div>
              <h2 className="text-base font-bold">My Saved Teams</h2>
              <p className="text-xs text-white/30 mt-0.5">{teams.length} team{teams.length !== 1 ? 's' : ''} saved</p>
            </div>
            <button onClick={onClose} className="text-white/25 hover:text-white transition-colors">
              <X size={18}/>
            </button>
          </div>

          <div className="max-h-[420px] overflow-y-auto">
            {teams.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3 text-white/20">
                <FolderOpen size={32}/>
                <p className="text-sm">No saved teams yet</p>
                <p className="text-xs text-white/15">Build a roster and click Save Team</p>
              </div>
            ) : (
              <div className="p-3 space-y-2">
                {teams.map(team => {
                  const starterCount = STARTER_SLOTS.filter(s => team.roster?.[s.id]).length;
                  const isCurrent    = team.id === currentTeamId;
                  return (
                    <div key={team.id}
                      className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                        isCurrent
                          ? 'bg-[#4d7cff]/10 border-[#4d7cff]/30'
                          : 'bg-white/[0.02] border-white/[0.06] hover:bg-white/[0.04]'
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold truncate">{team.name}</span>
                          {isCurrent && (
                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#4d7cff]/20 text-[#4d7cff] flex-shrink-0">
                              LOADED
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[10px] text-white/30">
                            {team.season} · Wk {team.week}
                          </span>
                          <span className="text-[10px] text-white/20">·</span>
                          <span className={`text-[10px] ${starterCount === 9 ? 'text-green-400' : 'text-white/30'}`}>
                            {starterCount}/9 starters
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <button
                          onClick={() => onLoad(team)}
                          className="px-3 py-1.5 text-xs rounded-lg bg-[#4d7cff]/10 border border-[#4d7cff]/25 text-[#4d7cff] hover:bg-[#4d7cff]/20 transition-all font-medium"
                        >
                          Load
                        </button>
                        <button
                          onClick={() => handleDelete(team.id)}
                          disabled={deleting === team.id}
                          className="w-7 h-7 flex items-center justify-center rounded-lg text-white/20 hover:text-red-400 hover:bg-red-500/10 transition-all disabled:opacity-40"
                        >
                          {deleting === team.id
                            ? <RefreshCw size={12} className="animate-spin"/>
                            : <Trash2 size={12}/>}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="px-5 py-3 border-t border-white/[0.07]">
            <button onClick={onClose}
              className="w-full py-2 rounded-xl text-sm text-white/40 hover:text-white hover:bg-white/5 transition-all">
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

/**
 * Modal for naming a new team before saving it.
 */
const SaveModal = ({ onSave, onClose, saving }) => {
  const [name, setName] = useState('');
  return (
    <>
      <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm" onClick={onClose}/>
      <div className="fixed inset-0 z-[101] flex items-center justify-center p-4 pointer-events-none">
        <div className="bg-[#0b1120] border border-white/[0.10] rounded-2xl shadow-[0_24px_80px_rgba(0,0,0,0.9)] w-full max-w-sm pointer-events-auto p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-bold">Save Team</h2>
            <button onClick={onClose} className="text-white/25 hover:text-white"><X size={16}/></button>
          </div>
          <input
            autoFocus
            type="text"
            placeholder="e.g. My Week 1 Lineup"
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && name.trim() && onSave(name)}
            className="w-full bg-white/[0.04] border border-white/[0.10] rounded-xl px-4 py-3 text-sm text-white placeholder-white/25 focus:outline-none focus:border-[#4d7cff]/50 mb-4"
          />
          <div className="flex gap-2">
            <button onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border border-white/[0.08] text-sm text-white/40 hover:text-white transition-all">
              Cancel
            </button>
            <button
              onClick={() => onSave(name)}
              disabled={!name.trim() || saving}
              className="flex-1 py-2.5 rounded-xl bg-[#4d7cff] hover:bg-[#3d6cef] text-white text-sm font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {saving ? <RefreshCw size={13} className="animate-spin"/> : <Save size={13}/>}
              Save
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================
const TeamBuilder = () => {
  const location = useLocation();
  const [players,         setPlayers]         = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [roster,          setRoster]          = useState({});
  const [activeSlot,      setActiveSlot]      = useState(null); // currently selected slot id
  const [searchQuery,     setSearchQuery]     = useState('');
  const [posFilter,       setPosFilter]       = useState('All');
  const [latestWeek,      setLatestWeek]      = useState(null);

  // Saved team state
  const [savedTeams,      setSavedTeams]      = useState([]);
  const [currentTeamId,   setCurrentTeamId]   = useState(null);
  const [currentTeamName, setCurrentTeamName] = useState('');
  const [showSaveModal,   setShowSaveModal]   = useState(false);
  const [showMyTeams,     setShowMyTeams]     = useState(false);
  const [saving,          setSaving]          = useState(false);
  const [saveConfirm,     setSaveConfirm]     = useState(false);
  const [poolLimit,       setPoolLimit]       = useState(75); // lazy-render threshold

  // Fetch the latest week's projections on mount
  useEffect(() => {
    api.getAvailableWeeks()
      .then(weeks => {
        if (!weeks?.length) return null;
        const sorted = [...weeks].sort((a, b) =>
          b.season !== a.season ? b.season - a.season : b.week - a.week
        );
        const latest = sorted[0];
        setLatestWeek(latest);
        return api.getProjections({ season: latest.season, week: latest.week });
      })
      .then(data => { if (data?.players) setPlayers(data.players); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Load the user's previously saved teams
  useEffect(() => {
    api.getFantasyTeams().then(setSavedTeams).catch(() => {});
  }, []);

  // If navigated here from the Dashboard detail card, auto-slot the incoming player
  useEffect(() => {
    const incoming = location.state?.player;
    if (!incoming || !players.length) return;
    const slot = SLOTS.find(s => !roster[s.id] && s.eligible.includes(incoming.pos));
    if (slot) setRoster(r => ({ ...r, [slot.id]: incoming }));
  }, [players]); // eslint-disable-line

  // Demo tour auto-fill: populate a complete starter lineup for demonstration
  useEffect(() => {
    const handler = () => {
      if (!players.length) return;
      const byPos  = (pos)          => players.find(p => p.pos === pos);
      const byPosN = (pos, skip=[]) => players.find(p => p.pos === pos && !skip.includes(p.id));
      const qb   = byPos('QB');
      const rb1  = byPosN('RB');
      const rb2  = byPosN('RB',  [rb1?.id].filter(Boolean));
      const wr1  = byPosN('WR');
      const wr2  = byPosN('WR',  [wr1?.id].filter(Boolean));
      const te   = byPos('TE');
      const used = [rb1,rb2,wr1,wr2,te].filter(Boolean).map(p=>p.id);
      const flex = players.find(p => ['RB','WR','TE'].includes(p.pos) && !used.includes(p.id));
      const k    = byPos('K');
      const dst  = byPos('DST');
      const filled = {};
      if (qb)   filled.qb   = qb;
      if (rb1)  filled.rb1  = rb1;
      if (rb2)  filled.rb2  = rb2;
      if (wr1)  filled.wr1  = wr1;
      if (wr2)  filled.wr2  = wr2;
      if (te)   filled.te   = te;
      if (flex) filled.flex = flex;
      if (k)    filled.k    = k;
      if (dst)  filled.dst  = dst;
      setRoster(filled);
      setActiveSlot(null);
    };
    window.addEventListener('demo:teamBuilderAutoFill', handler);
    return () => window.removeEventListener('demo:teamBuilderAutoFill', handler);
  }, [players]);

  // --- Derived state ---

  // IDs of all players already on the roster (to exclude from the pool)
  const rostered = useMemo(() =>
    new Set(Object.values(roster).filter(Boolean).map(p => p.id)),
  [roster]);

  // Filtered player pool: exclude rostered players, then apply position and search filters
  const pool = useMemo(() => {
    let list = players.filter(p => !rostered.has(p.id));
    if (posFilter !== 'All') list = list.filter(p => p.pos === posFilter);
    if (searchQuery) list = list.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()));
    return list;
  }, [players, rostered, posFilter, searchQuery]);

  const activeSlotDef = SLOTS.find(s => s.id === activeSlot);
  const starters      = STARTER_SLOTS.map(s => roster[s.id]).filter(Boolean);
  const teamProj      = starters.reduce((a, p) => a + (p.proj   ?? 0), 0);
  const teamFloor     = starters.reduce((a, p) => a + (p.ciLow  ?? 0), 0);
  const teamCeiling   = starters.reduce((a, p) => a + (p.ciHigh ?? 0), 0);
  const rosterCount   = Object.values(roster).filter(Boolean).length;

  // --- Slot / player interaction ---

  const handleSlotClick = (slotId) => {
    if (roster[slotId]) {
      // Clicking a filled slot removes the player and re-activates the slot
      setRoster(r => { const next = { ...r }; delete next[slotId]; return next; });
      setActiveSlot(slotId);
    } else {
      setActiveSlot(slotId === activeSlot ? null : slotId);
    }
  };

  const handlePlayerClick = (player) => {
    if (activeSlot) {
      // Position eligibility check
      if (!activeSlotDef?.eligible.includes(player.pos)) return;
      const filled = activeSlot;
      setRoster(r => ({ ...r, [filled]: player }));
      // Auto-advance to the next empty starter slot
      const nextEmpty = STARTER_SLOTS.find(s => s.id !== filled && !roster[s.id]);
      setActiveSlot(nextEmpty?.id || null);
    } else {
      // No slot active — find the first eligible empty slot
      const slot = SLOTS.find(s => !roster[s.id] && s.eligible.includes(player.pos));
      if (slot) setRoster(r => ({ ...r, [slot.id]: player }));
    }
  };

  // --- Persistence handlers ---

  const handleSaveNew = async (name) => {
    setSaving(true);
    try {
      const saved = await api.saveFantasyTeam({
        name: name.trim(),
        season: latestWeek?.season ?? 2025,
        week:   latestWeek?.week   ?? 1,
        roster,
      });
      setSavedTeams(t => [saved, ...t]);
      setCurrentTeamId(saved.id);
      setCurrentTeamName(saved.name);
      setShowSaveModal(false);
      setSaveConfirm(true);
      setTimeout(() => setSaveConfirm(false), 2000);
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!currentTeamId) return;
    setSaving(true);
    try {
      const updated = await api.updateFantasyTeam(currentTeamId, { roster });
      setSavedTeams(t => t.map(tm => tm.id === currentTeamId ? updated : tm));
      setSaveConfirm(true);
      setTimeout(() => setSaveConfirm(false), 2000);
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (teamId) => {
    await api.deleteFantasyTeam(teamId);
    setSavedTeams(t => t.filter(tm => tm.id !== teamId));
    if (currentTeamId === teamId) {
      setCurrentTeamId(null);
      setCurrentTeamName('');
    }
  };

  const loadTeam = (team) => {
    setRoster(team.roster || {});
    setCurrentTeamId(team.id);
    setCurrentTeamName(team.name);
    setActiveSlot(null);
    setShowMyTeams(false);
  };

  const clearAll = () => {
    setRoster({});
    setActiveSlot(null);
    setCurrentTeamId(null);
    setCurrentTeamName('');
  };

  // --- CSV export ---
  const exportCSV = () => {
    const rows = [
      ['Slot','Player','Pos','Team','Projected','Floor','Ceiling','Risk'],
      ...SLOTS.map(s => {
        const p = roster[s.id];
        if (!p) return [s.label,'','','','','','',''];
        const risk = getRisk(p.ciLow, p.ciHigh, p.proj);
        return [s.label, p.name, p.pos, p.team, p.proj?.toFixed(1) ?? '', p.ciLow?.toFixed(1) ?? '', p.ciHigh?.toFixed(1) ?? '', risk.label];
      }),
    ];
    const a   = document.createElement('a');
    a.href    = URL.createObjectURL(new Blob([rows.map(r => r.join(',')).join('\n')], { type: 'text/csv' }));
    a.download = `${currentTeamName || 'my_fantasy_team'}.csv`;
    a.click();
  };

  /**
   * Renders a single roster slot row inside the lineup panel.
   * Active slot is highlighted blue; eligible empty slots glow when a slot
   * is active so the user knows where they can drop the selected player.
   */
  const SlotRow = ({ slot }) => {
    const player   = roster[slot.id];
    const isActive = activeSlot === slot.id;
    const eligible = !activeSlotDef || activeSlotDef.eligible.some(e => slot.eligible.includes(e));
    const s        = player ? posStyle(player.pos) : null;
    const risk     = player ? getRisk(player.ciLow, player.ciHigh, player.proj) : null;

    return (
      <div
        onClick={() => handleSlotClick(slot.id)}
        className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl cursor-pointer transition-all border ${
          isActive
            ? 'bg-[#4d7cff]/15 border-[#4d7cff]/50'
            : player
              ? 'bg-white/[0.03] border-white/[0.06] hover:bg-white/[0.05]'
              : activeSlot && eligible
                ? 'bg-white/[0.02] border-[#4d7cff]/20 border-dashed hover:border-[#4d7cff]/40'
                : 'bg-white/[0.02] border-white/[0.04] hover:border-white/[0.08]'
        }`}
      >
        <div className={`w-11 text-[10px] font-black uppercase tracking-widest flex-shrink-0 ${
          slot.bench ? 'text-white/20' : slot.label === 'FLEX' ? 'text-purple-400' : posStyle(slot.eligible[0]).text
        }`}>
          {slot.label}
        </div>
        {player ? (
          <>
            <PlayerAvatar player={player} size={7}/>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate leading-tight">{player.name}</div>
              <ProjBar player={player}/>
            </div>
            <div className="text-right flex-shrink-0">
              <div className="text-sm font-bold text-[#4d7cff]">{player.proj?.toFixed(1)}</div>
              <div className={`text-[9px] ${risk.color}`}>{risk.label}</div>
            </div>
            <button
              onClick={e => {
                e.stopPropagation();
                setRoster(r => { const n = { ...r }; delete n[slot.id]; return n; });
              }}
              className="text-white/15 hover:text-red-400 transition-colors flex-shrink-0 ml-1"
            >
              <X size={11}/>
            </button>
          </>
        ) : (
          <div className="text-xs text-white/20 italic flex-1">
            {isActive ? `Pick a ${slot.label === 'FLEX' ? 'RB / WR / TE' : slot.label} →` : 'Empty'}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#050d1f] text-white font-sans pb-16">
      <Navbar/>

      {showMyTeams && (
        <MyTeamsModal
          teams={savedTeams}
          currentTeamId={currentTeamId}
          onLoad={loadTeam}
          onDelete={handleDelete}
          onClose={() => setShowMyTeams(false)}
        />
      )}

      {showSaveModal && (
        <SaveModal
          saving={saving}
          onSave={handleSaveNew}
          onClose={() => setShowSaveModal(false)}
        />
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 pt-6 sm:pt-8">

        {/* Page header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-6 gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2 h-2 rounded-full bg-[#4d7cff] animate-pulse"/>
              <span className="text-[10px] text-white/30 uppercase tracking-widest font-medium">
                {latestWeek ? `${latestWeek.season} · Week ${latestWeek.week} Projections` : 'Loading projections...'}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-black">Fantasy Team Builder</h1>
              {currentTeamName && (
                <span className="text-sm text-white/40 font-medium">
                  — <span className="text-white/60">{currentTeamName}</span>
                </span>
              )}
            </div>
            <p className="text-sm text-white/35 mt-1">Click a roster slot, then select a player from the pool</p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* Open saved teams list */}
            <button
              onClick={() => setShowMyTeams(true)}
              className="px-3 py-2 text-xs rounded-xl border border-white/[0.08] text-white/50 hover:text-white hover:border-white/20 transition-all flex items-center gap-1.5"
            >
              <FolderOpen size={12}/>
              My Teams
              {savedTeams.length > 0 && (
                <span className="bg-[#4d7cff]/20 text-[#4d7cff] text-[9px] font-bold px-1.5 py-0.5 rounded-full">
                  {savedTeams.length}
                </span>
              )}
            </button>

            {/* Update existing team or save new one */}
            {currentTeamId ? (
              <button
                onClick={handleUpdate}
                disabled={saving}
                className="px-3 py-2 text-xs rounded-xl bg-[#4d7cff]/10 border border-[#4d7cff]/30 hover:bg-[#4d7cff]/20 text-[#4d7cff] transition-all flex items-center gap-1.5 disabled:opacity-50"
              >
                {saveConfirm
                  ? <><Check size={12}/> Saved</>
                  : saving
                    ? <><RefreshCw size={12} className="animate-spin"/> Saving...</>
                    : <><Save size={12}/> Update Team</>}
              </button>
            ) : (
              <button
                onClick={() => setShowSaveModal(true)}
                disabled={!rosterCount}
                className="px-3 py-2 text-xs rounded-xl bg-[#4d7cff]/10 border border-[#4d7cff]/30 hover:bg-[#4d7cff]/20 text-[#4d7cff] transition-all flex items-center gap-1.5 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                {saveConfirm ? <><Check size={12}/> Saved!</> : <><Save size={12}/> Save Team</>}
              </button>
            )}

            <button
              onClick={clearAll}
              className="px-3 py-2 text-xs rounded-xl border border-white/[0.08] text-white/40 hover:text-white hover:border-white/20 transition-all"
            >
              Clear All
            </button>

            <button
              onClick={exportCSV}
              disabled={!starters.length}
              className="px-3 py-2 text-xs rounded-xl border border-white/[0.08] text-white/40 hover:text-white hover:border-white/20 transition-all flex items-center gap-1.5 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <Download size={12}/> CSV
            </button>
          </div>
        </div>

        {/* Team projection summary cards */}
        <div data-tour="team-summary" className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          {[
            { label: 'Proj Total',   value: teamProj    ? teamProj.toFixed(1)    : '—', color: 'text-[#4d7cff]'    },
            { label: 'Team Floor',   value: teamFloor   ? teamFloor.toFixed(1)   : '—', color: 'text-green-400'    },
            { label: 'Team Ceiling', value: teamCeiling ? teamCeiling.toFixed(1) : '—', color: 'text-yellow-400'   },
            { label: 'Starters',     value: `${starters.length} / 9`,                   color: starters.length === 9 ? 'text-green-400' : 'text-white/50' },
            { label: 'On Roster',    value: `${rosterCount} / 15`,                       color: 'text-white/50'     },
          ].map(({ label, value, color }) => (
            <div key={label} className="bg-[#0c1526] border border-white/[0.07] rounded-2xl p-4">
              <div className={`text-xl font-black ${color}`}>{value}</div>
              <div className="text-[10px] text-white/30 uppercase tracking-widest mt-0.5">{label}</div>
            </div>
          ))}
        </div>

        {/* Main layout: roster panel left, player pool right */}
        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-5 items-start">

          {/* Roster panel */}
          <div data-tour="team-roster" className="bg-[#0b1120] border border-white/[0.07] rounded-2xl p-4">
            <div className="text-[10px] text-white/25 uppercase tracking-widest font-bold mb-2">Starters</div>
            <div className="space-y-1">
              {STARTER_SLOTS.map(slot => <SlotRow key={slot.id} slot={slot}/>)}
            </div>
            <div className="text-[10px] text-white/25 uppercase tracking-widest font-bold mt-4 mb-2 pt-3 border-t border-white/[0.05]">Bench</div>
            <div className="space-y-1">
              {BENCH_SLOTS.map(slot => <SlotRow key={slot.id} slot={slot}/>)}
            </div>
          </div>

          {/* Player pool */}
          <div className="bg-[#0b1120] border border-white/[0.07] rounded-2xl p-4">
            <div className="flex flex-col sm:flex-row gap-2 mb-3">
              <div className="relative flex-1">
                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/25"/>
                <input
                  type="text" value={searchQuery}
                  onChange={e => { setSearchQuery(e.target.value); setPoolLimit(75); }}
                  placeholder="Search players..."
                  className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl pl-9 pr-8 py-2.5 text-sm text-white placeholder-white/25 focus:outline-none focus:border-[#4d7cff]/40"
                />
                {searchQuery && (
                  <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/25 hover:text-white">
                    <X size={12}/>
                  </button>
                )}
              </div>
              {/* Position filter tabs */}
              <div className="flex bg-white/[0.04] p-0.5 rounded-xl gap-0.5 flex-wrap">
                {POSITIONS.map(p => (
                  <button key={p} onClick={() => { setPosFilter(p); setPoolLimit(75); }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${posFilter === p ? 'bg-[#4d7cff] text-white' : 'text-white/40 hover:text-white'}`}>
                    {p}
                  </button>
                ))}
              </div>
            </div>

            {/* Active slot indicator */}
            {activeSlot && (
              <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-[#4d7cff]/10 border border-[#4d7cff]/25 rounded-xl">
                <div className="w-1.5 h-1.5 rounded-full bg-[#4d7cff] animate-pulse"/>
                <span className="text-xs text-[#4d7cff] flex-1">
                  Filling <strong>{activeSlotDef?.label}</strong>
                  {activeSlotDef?.label === 'FLEX' ? ' (RB / WR / TE)' : ''} — click a player
                </span>
                <button onClick={() => setActiveSlot(null)} className="text-[#4d7cff]/40 hover:text-[#4d7cff]"><X size={12}/></button>
              </div>
            )}

            {/* Column headers */}
            <div className="grid grid-cols-[1fr_60px_80px_36px] gap-2 px-3 pb-1.5 text-[10px] text-white/25 uppercase tracking-widest font-bold border-b border-white/[0.05] mb-1">
              <span>Player</span>
              <span className="text-right">Proj</span>
              <span className="text-center">Floor–Ceil</span>
              <span className="text-right">Risk</span>
            </div>

            {loading ? (
              // Skeleton loader while projections are fetching
              <div className="space-y-1.5 pt-2">
                {Array.from({ length: 14 }).map((_, i) => (
                  <div key={i} className="h-12 bg-white/[0.03] rounded-xl animate-pulse" style={{ opacity: 1 - i * 0.06 }}/>
                ))}
              </div>
            ) : (
              <div data-tour="player-pool" className="space-y-0.5 max-h-[640px] overflow-y-auto pr-0.5">
                {pool.slice(0, poolLimit).map(player => {
                  const s          = posStyle(player.pos);
                  const risk       = getRisk(player.ciLow, player.ciHigh, player.proj);
                  // Gray out players ineligible for the currently active slot
                  const ineligible = activeSlotDef && !activeSlotDef.eligible.includes(player.pos);
                  return (
                    <div
                      key={player.id}
                      onClick={() => !ineligible && handlePlayerClick(player)}
                      className={`grid grid-cols-[1fr_60px_80px_36px] gap-2 items-center px-3 py-2.5 rounded-xl transition-all ${
                        ineligible ? 'opacity-20 cursor-not-allowed' : 'cursor-pointer hover:bg-white/[0.04]'
                      }`}
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <PlayerAvatar player={player} size={8}/>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold truncate leading-tight">{player.name}</div>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${s.bg} ${s.text}`}>{player.pos}</span>
                            <span className="text-[10px] text-white/25">{player.team}</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="text-sm font-bold text-[#4d7cff]">{player.proj?.toFixed(1)}</span>
                      </div>
                      <div className="text-center text-[10px] text-white/30">
                        {player.ciLow != null && player.ciHigh != null
                          ? `${player.ciLow.toFixed(1)} – ${player.ciHigh.toFixed(1)}` : '—'}
                      </div>
                      <div className={`text-right text-[10px] font-bold ${risk.color}`}>{risk.label}</div>
                    </div>
                  );
                })}
                {pool.length === 0 && (
                  <div className="text-center py-16 text-white/20 text-sm">
                    {activeSlot ? 'No eligible players available' : 'No players match your filters'}
                  </div>
                )}
                {pool.length > poolLimit && (
                  <button
                    onClick={() => setPoolLimit(l => l + 75)}
                    className="w-full py-2.5 mt-1 rounded-xl text-xs text-white/40 hover:text-white hover:bg-white/5 transition-all"
                  >
                    Load more ({pool.length - poolLimit} remaining)
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default TeamBuilder;
